import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon, PrismMetricBlock } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, or } from 'ember-truth-helpers';
import GeneralPercentageChangeValue from 'lumen/components/general/percentage-change-value';
import GeneralTooltipsTooltipEvents from 'lumen/components/general/tooltips/tooltip-events';
import formatOtbValue from 'lumen/helpers/format-otb-value';
import hasFeature from 'lumen/helpers/has-feature';
import type { Holiday, Period } from 'lumen/models';

interface GeneralTooltipsTooltipRatesHeaderSectionSignature {
  Args: {
    formattedMedian?: string;
    period?: Period;
  };
}

export default class GeneralTooltipsTooltipRatesHeaderSection extends Component<GeneralTooltipsTooltipRatesHeaderSectionSignature> {
  @service declare access: Services['access'];

  get holidays(): Holiday[] {
    const { period } = this.args;

    if (period?.day?.holidays) {
      return [...period.day.holidays];
    }

    return [];
  }

  get showDemandIndicator(): boolean {
    const { period } = this.args;

    if (!this.access.hasFeature('subscription.demand')) {
      return false;
    }

    return Boolean(period?.hasDemandData) && !period?.day?.isPast;
  }

  <template>
    <div class="tooltip-section">
      <div>
        <div
          class="tooltip-section tooltip-section--no-border grid auto-cols-fr grid-flow-col"
        >
          {{#if (hasFeature "pms.otb.view")}}
            <PrismMetricBlock>
              <:value>
                <span>
                  {{formatOtbValue
                    @period.day.occupancy.occupancyPercentage
                    style="percent"
                  }}
                </span>
                {{#if @period.showOccupancyChange}}
                  <GeneralPercentageChangeValue
                    @changeValue={{@period.occupancyChange}}
                  />
                {{/if}}
              </:value>
              <:label>
                <div class="flex items-center justify-center gap-100">
                  {{t "rates.my_OTB"}}
                  {{#if
                    (and
                      @period.isCurrentOrFutureDay
                      @period.day.occupancy.isOtbOutOfSync
                    )
                  }}
                    <PrismIcon @name="sync-disabled" @size="extra-small" />
                  {{/if}}
                </div>
              </:label>
            </PrismMetricBlock>
            {{#if @period.showMarketBenchmark}}
              <PrismMetricBlock>
                <:value>
                  <span>
                    {{formatOtbValue
                      @period.day.occupancy.benchmarkValueNormalized
                      style="percent"
                    }}
                  </span>
                  {{#if @period.showMarketBenchmarkChange}}
                    <GeneralPercentageChangeValue
                      @changeValue={{@period.benchmarkChange}}
                    />
                  {{/if}}
                </:value>
                <:label>
                  <div class="flex items-center justify-center gap-100">
                    {{t "rates.tooltip.market_otb"}}
                    {{#if
                      (and
                        @period.isCurrentOrFutureDay
                        @period.day.occupancy.isMarketOtbOutOfSync
                      )
                    }}
                      <PrismIcon @name="sync-disabled" @size="extra-small" />
                    {{/if}}
                  </div>
                </:label>
              </PrismMetricBlock>
            {{/if}}
          {{else if
            (or
              (hasFeature "otb.empty_placeholder")
              (hasFeature "otb.data_validation")
            )
          }}
            <PrismMetricBlock>
              <:value>
                <span
                  class={{if
                    (hasFeature "otb.empty_placeholder")
                    "text-brand font-bold"
                  }}
                >
                  {{t "general.missing"}}
                </span>
              </:value>
              <:label>
                <span
                  class={{if
                    (hasFeature "otb.empty_placeholder")
                    "text-brand font-bold"
                  }}
                >
                  {{t "rates.my_OTB"}}</span>
              </:label>
            </PrismMetricBlock>
          {{/if}}
          {{#if this.showDemandIndicator}}
            <PrismMetricBlock>
              <:value>
                <div class="flex items-center gap-100">
                  <div
                    class="identifier-square
                      {{@period.day.demand.demandLevelClass}}"
                  ></div>
                  {{@period.day.demand.formattedValue}}
                </div>
              </:value>
              <:label>{{t "demand.market_demand"}}</:label>
            </PrismMetricBlock>
          {{/if}}
          {{#if @formattedMedian}}
            <PrismMetricBlock>
              <:value>{{@formattedMedian}}</:value>
              <:label>{{t "rates.median_comp_rate"}}</:label>
            </PrismMetricBlock>
          {{/if}}
        </div>
        {{#if @period.showReferenceChangeText}}
          <div class="tooltip-section tooltip-section--no-border">
            <p class="text-200--regular m-200">
              {{@period.referenceChangeText}}
            </p>
          </div>
        {{/if}}
      </div>
    </div>

    {{#if this.access.hasEvents}}
      <GeneralTooltipsTooltipEvents
        @events={{@period.day.calendarEvents}}
        @holidays={{this.holidays}}
        @title="market_insight.detail_pane.tabs.events_holidays"
      />
    {{/if}}
  </template>
}
